import { For, Show, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { faQrcode } from "@fortawesome/free-solid-svg-icons";
import { cx } from "../../styled-system/css";
import { originalImageUrl } from "../data";
import { styles } from "../styles";
import { findCatalogListRow, isCatalogListRowOwned } from "../features/catalog/selectors";
import { GROUP_ORDER, type CompareGroup, type CoordEntry, type CoordItem, type Selection } from "../types";
import { QrList, type QrDisplayItem } from "./QrList";
import { FontAwesomeIcon } from "./FontAwesomeIcon";
import type { MixCoordinateRegistration } from "../features/mixes/types";

type Props = {
  open: boolean;
  entries: CoordEntry[];
  selection: Selection;
  showQr: boolean;
  qrCycleIntervalMs: number;
  canRegisterMix: boolean;
  brandOptions: string[];
  onClose: () => void;
  onClear: () => void;
  onRemove: (group: CompareGroup, item: CoordItem) => void;
  onRegisterMix: (registration: MixCoordinateRegistration) => void;
};

export function CompareDrawer(props: Props) {
  const [indices, setIndices] = createSignal<Record<CompareGroup, number>>(
    Object.fromEntries(GROUP_ORDER.map((group) => [group, 0])) as Record<CompareGroup, number>
  );
  const [onePieceMode, setOnePieceMode] = createSignal(false);
  const [qrVisible, setQrVisible] = createSignal(false);
  const [mixName, setMixName] = createSignal("");
  const [mixBrand, setMixBrand] = createSignal("");
  let closeButton!: HTMLButtonElement;
  let wasOpen = false;

  const visibleGroups = (): CompareGroup[] => onePieceMode()
    ? ["アクセ", "ワンピ", "シューズ"]
    : ["アクセ", "トップス", "ボトムス", "シューズ"];

  const currentFor = (group: CompareGroup): CoordItem | undefined => {
    const items = props.selection[group];
    const index = Math.min(indices()[group], Math.max(0, items.length - 1));
    return items[index];
  };

  const qrItems = (): QrDisplayItem[] => {
    const result: QrDisplayItem[] = [];
    const addedRows = new Set<string>();
    for (const group of visibleGroups()) {
      const item = currentFor(group);
      if (!item) continue;
      const entry = props.entries.find((candidate) => candidate.items.some((entryItem) =>
        entryItem === item || (entryItem.id === item.id && entryItem.url === item.url)
      ));
      const row = entry ? findCatalogListRow(entry, item) : undefined;
      if (entry && row) {
        const rowKey = `${entry.key}\n${row.key}`;
        if (addedRows.has(rowKey)) continue;
        addedRows.add(rowKey);
        result.push({
          label: row.label,
          name: entry.name,
          id: row.id,
          qr: isCatalogListRowOwned(row) ? row.qr : "",
          missingLabel: isCatalogListRowOwned(row) ? "QR未登録" : "未所持",
        });
        continue;
      }
      result.push({
        label: group,
        name: item.name,
        id: item.id,
        qr: item.owned === false ? "" : (item.qr ?? ""),
        missingLabel: item.owned === false ? "未所持" : "QR未登録",
      });
    }
    return result.some((item) => Boolean(item.qr))
      ? result
      : result.filter((item) => item.missingLabel !== "未所持");
  };

  const displayedItems = (): CoordItem[] => visibleGroups()
    .map(currentFor)
    .filter((item): item is CoordItem => item !== undefined);

  const registerMix = (event: SubmitEvent) => {
    event.preventDefault();
    props.onRegisterMix({ name: mixName(), brand: mixBrand(), items: displayedItems() });
    setMixName("");
  };

  const closeDrawer = () => {
    setQrVisible(false);
    props.onClose();
  };

  const move = (group: CompareGroup, amount: number) => {
    const length = props.selection[group].length;
    if (!length) return;
    setIndices((current) => ({ ...current, [group]: (current[group] + amount + length) % length }));
  };

  createEffect(() => {
    const open = props.open;
    if (open && !wasOpen) {
      setOnePieceMode(props.selection["トップス"].length === 0 && props.selection["ボトムス"].length === 0);
    }
    wasOpen = open;
  });

  createEffect(() => {
    if (props.open) queueMicrotask(() => closeButton?.focus());
  });

  createEffect(() => {
    if (!props.showQr) setQrVisible(false);
  });

  onMount(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && props.open) closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    onCleanup(() => window.removeEventListener("keydown", onKeyDown));
  });

  return (
    <>
      <Show when={props.open}>
        <button class={styles.backdrop} type="button" aria-label="選択コーデ確認を閉じる" onClick={closeDrawer} />
      </Show>
      <aside
        class={cx(styles.drawer, props.open ? styles.drawerOpen : styles.drawerClosed)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="compare-title"
        aria-hidden={!props.open}
        inert={!props.open}
      >
        <header class={styles.drawerHeader}>
          <h2 id="compare-title" class={styles.drawerTitle}>選択コーデ確認</h2>
          <div class={styles.drawerActions}>
            <button class={styles.button} type="button" onClick={props.onClear}>クリア</button>
            <button ref={closeButton} class={styles.iconButton} type="button" aria-label="閉じる" onClick={closeDrawer}>✕</button>
          </div>
        </header>
        <div class={styles.drawerBody}>
          <Show when={props.open}>
            <Show when={!qrVisible()} fallback={<QrList items={qrItems()} cycleIntervalMs={props.qrCycleIntervalMs} />}>
              <div class={styles.coordViewport}>
                <div class={styles.modeBar}>
                  <label class={styles.modeSwitch}>
                    <input type="checkbox" checked={onePieceMode()} onChange={(event) => setOnePieceMode(event.currentTarget.checked)} />
                    <span>ワンピ表示</span>
                  </label>
                </div>
                <div class={styles.coordGroups}>
                  <For each={visibleGroups()}>
                    {(group) => {
                      const items = () => props.selection[group];
                      const index = () => Math.min(indices()[group], Math.max(0, items().length - 1));
                      const current = () => items()[index()];
                      return (
                        <Show when={current()}>
                          {(item) => (
                            <section class={styles.group}>
                              <div class={styles.carousel}>
                                <button class={styles.compactIconButton} type="button" aria-label={`${group}の前の候補`} onClick={() => move(group, -1)}>◀</button>
                                <div class={styles.stage}>
                                  <div class={styles.stageToolbar}>
                                    <span class={styles.groupBadge}>{group}　{index() + 1}/{items().length}</span>
                                    <button class={styles.removeCandidate} type="button" onClick={() => props.onRemove(group, item())}>外す</button>
                                  </div>
                                  <div class={styles.stageBox}><img class={styles.image} src={originalImageUrl(item().url)} alt={`${item().name} ${group}`} loading="lazy" decoding="async" /></div>
                                </div>
                                <button class={styles.compactIconButton} type="button" aria-label={`${group}の次の候補`} onClick={() => move(group, 1)}>▶</button>
                              </div>
                            </section>
                          )}
                        </Show>
                      );
                    }}
                  </For>
                  <Show when={GROUP_ORDER.every((group) => props.selection[group].length === 0)}>
                    <p class={cx(styles.empty, styles.drawerEmpty)}>カードのパーツ画像を選ぶと、ここで確認できます<br/>コーデ画像長押し、あるいはリスト表示で選択できます</p>
                  </Show>
                </div>
              </div>
            </Show>
            <section
              class={styles.selectionSummary}
              classList={{ [styles.selectionSummaryWithoutQr]: !props.showQr }}
              aria-label="選択中のコーデ名"
            >
              <div class={styles.selectionNames}>
                <For each={visibleGroups()}>
                  {(group) => <Show when={currentFor(group)}>{(item) => <p><strong>{group}：</strong>{item().name}</p>}</Show>}
                </For>
              </div>
            </section>
            <Show when={props.canRegisterMix}>
            <form class={styles.mixRegistrationForm} onSubmit={registerMix}>
              <div class={styles.mixRegistrationHeading}>
                <strong>表示中のコーデをミックスコーデ登録</strong>
              </div>
              <div class={styles.mixRegistrationFields}>
                <label>
                  <span>登録名</span>
                  <input
                    class={styles.input}
                    value={mixName()}
                    maxlength={30}
                    placeholder="ミックスコーデ名"
                    onInput={(event) => setMixName(event.currentTarget.value)}
                    disabled={!props.canRegisterMix}
                  />
                </label>
                <label>
                  <span>ブランド</span>
                  <select
                    class={styles.input}
                    value={mixBrand()}
                    onChange={(event) => setMixBrand(event.currentTarget.value)}
                    disabled={!props.canRegisterMix}
                  >
                    <option value="">選択してください</option>
                    <For each={props.brandOptions}>{(brand) => <option value={brand}>{brand}</option>}</For>
                  </select>
                </label>
                <button
                  class={styles.mixRegistrationButton}
                  type="submit"
                  disabled={!props.canRegisterMix || !mixName().trim() || !mixBrand() || displayedItems().length === 0}
                >登録</button>
              </div>
            </form>
            </Show>
          </Show>
        </div>
        <Show when={props.open && props.showQr}>
          <button
            class={styles.qrDisplayButton}
            type="button"
            title={qrVisible() ? "コーデ表示に戻る" : "QRを表示"}
            aria-label={qrVisible() ? "コーデ表示に戻る" : "QRを表示"}
            aria-pressed={qrVisible()}
            onClick={() => setQrVisible((visible) => !visible)}
          >
            <FontAwesomeIcon icon={faQrcode} class={styles.qrButtonIcon} />
          </button>
        </Show>
      </aside>
    </>
  );
}
